// Where a visitor first came from. The middleware records it once, on their first page, in a first-party cookie
// (pc_entry, 90 days); the sign-up sends it with the account (account-completion.js), so each account knows its
// channel: search, social, an AI assistant, another site, a paid click, or a direct visit. The backend keeps it in
// growth.signups.entry_* (myapp/entry_source.py; c_data migration 1070) and reports it in report.signup_sources and
// report.revenue_by_source.
//
// Hosts and paths only: never a full URL, a query string, or a click id's value, and never shared with anyone. Recorded
// for every visitor as a functional first-party cookie, not part of the analytics consent choice (the privacy policy's
// cookie section names it), unless the strict consent switch is on (NEXT_PUBLIC_STRICT_CONSENT, consent-policy.js): then
// the middleware records it only for a visitor whose tracking is allowed. No browser dependencies: the middleware
// imports it.
import { isKnownLandingBot, isLandingPrefetch } from './landing-bots.js'

export const ENTRY_COOKIE = 'pc_entry'
const ENTRY_COOKIE_MAX_AGE = 60 * 60 * 24 * 90
// The ad click ids a link can carry (the backend's CLICK_IDS): their presence marks a paid click.
export const CLICK_IDS = [
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
  'ttclid',
  'twclid',
  'rdt_cid',
  'li_fat_id',
  'sccid',
]
export const OWN_HOST = /(^|\.)promptchan\.com$/
const HOST = /^[a-z0-9-]+(\.[a-z0-9-]+)*$/
const PATH = /^\/[!-~]*$/

// A utm tag in the one form the account keeps: lower case, letters, digits and ._~+- only, at most 100 characters.
export function tag(value) {
  const clean = (value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9._~+-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100)
  return clean || undefined
}

/**
 * The entry a first page records: { referrer_host, path, utm_source, utm_medium, utm_campaign, click_id, returning, at }
 * (at in Unix seconds; fields without a value left out). Null when the referrer is one of our own pages: that visit
 * began before recording did, and its source is unknown.
 */
export function entryFromRequest(url, referrer, returning, now = Date.now()) {
  let host
  try {
    if (referrer) host = new URL(referrer).hostname.toLowerCase()
  } catch {
    /* an unreadable referrer is no source */
  }
  if (host && (OWN_HOST.test(host) || host === url.hostname)) return null
  const params = new Map(
    [...url.searchParams].map(([key, value]) => [key.toLowerCase(), value]),
  )
  const entry = { at: Math.floor(now / 1000), returning: Boolean(returning) }
  if (host && host.length <= 253 && HOST.test(host)) entry.referrer_host = host
  if (url.pathname.length <= 200 && PATH.test(url.pathname))
    entry.path = url.pathname
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign']) {
    const value = tag(params.get(key))
    if (value) entry[key] = value
  }
  const clickId = CLICK_IDS.find((key) => params.has(key))
  if (clickId) entry.click_id = clickId
  return entry
}

/**
 * Record the visitor's entry on `res` if this request is their first page: a real page load (not a prefetch, a
 * client-side navigation or a crawler) with no entry recorded yet.
 */
export function recordEntry(req, res) {
  const headers = req.headers
  if (req.method !== 'GET' || req.cookies.get(ENTRY_COOKIE)) return
  const dest = headers.get('sec-fetch-dest')
  const page = dest
    ? dest === 'document'
    : (headers.get('accept') || '').includes('text/html')
  if (
    !page ||
    headers.has('rsc') ||
    isLandingPrefetch(headers) ||
    isKnownLandingBot(headers)
  )
    return
  // The country cookie goes on every response (middleware.js): with it, they have been here before.
  const entry = entryFromRequest(
    req.nextUrl,
    headers.get('referer'),
    Boolean(req.cookies.get('country')),
  )
  if (!entry) return
  res.cookies.set(ENTRY_COOKIE, encodeURIComponent(JSON.stringify(entry)), {
    path: '/',
    sameSite: 'lax',
    secure: req.nextUrl.protocol === 'https:',
    httpOnly: false,
    maxAge: ENTRY_COOKIE_MAX_AGE,
  })
}

/** The recorded entry from the cookie's value, or null. */
export function parseEntryCookie(raw) {
  if (!raw || typeof raw !== 'string') return null
  try {
    const value = JSON.parse(
      raw.startsWith('{') ? raw : decodeURIComponent(raw),
    )
    return value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      Number.isInteger(value.at)
      ? value
      : null
  } catch {
    return null
  }
}
